import { useState, useEffect } from "react";
import logger from '@/utils/logger';
import { fetchContentJson, extractPanelData, getFieldValue } from "@/utils/contentJsonParser";

interface ClientIcon {
	url: string;
	data?: { url: string };
}

export default function ClientsServed() {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [itemsPerView, setItemsPerView] = useState(6);

	const [clientIconsUrls, setClientIconsUrls] = useState<string[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [sectionTag, setSectionTag] = useState<string>("");
	const [sectionTitleLine1, setSectionTitleLine1] = useState<string>("");
	const [sectionTitleLine2, setSectionTitleLine2] = useState<string>("");
	const [sectionDescription, setSectionDescription] = useState<string>("");
	const [globalBrandsServed, setGlobalBrandsServed] = useState<string>("");
	const [impressionsGenerated, setImpressionsGenerated] = useState<string>("");
	const [satisfactionRate, setSatisfactionRate] = useState<string>("");

	useEffect(() => {
		const fetchClientIcons = async () => {
			try {
				setError(null);
				setLoading(true);

				const data = await fetchContentJson();
				
				// Extract the clients panel
				const clientsPanel = extractPanelData(data, "clientsWeHaveServedPanel");

				if (!clientsPanel) {
					throw new Error("Client logos section not found");
				}

				// Extract section fields
				setSectionTag(getFieldValue(clientsPanel, "clients_title", "Global Trust"));
				setSectionTitleLine1(getFieldValue(clientsPanel, "clients_primary_header", "Trusted by"));
				setSectionTitleLine2(getFieldValue(clientsPanel, "clients_secondary_header", "Leading Brands"));
				setSectionDescription(getFieldValue(clientsPanel, "clients_description", "Join hundreds of companies that have amplified their reach with our premium billboard advertising platform"));

				// Extract stats
				setGlobalBrandsServed(getFieldValue(clientsPanel, "clients_global_brands_served", ""));
				setImpressionsGenerated(getFieldValue(clientsPanel, "clients_impressions_generated", ""));
				setSatisfactionRate(getFieldValue(clientsPanel, "clients_satisfaction_rate", ""));

				// Extract client icons
				const clientIconsValue = getFieldValue(clientsPanel, "ClientsIconsEntries", []);
				if (!clientIconsValue || !Array.isArray(clientIconsValue) || clientIconsValue.length === 0) {
					setClientIconsUrls([]);
					return;
				}

				const clientIconsUrls = clientIconsValue
					.flatMap((entry: any) => entry.ClientIcons)
					.map((icon: ClientIcon) => {
						if (icon.url) {
							return `${import.meta.env.VITE_APP_FOLDER_PATH}/${icon.url}`;
						} else if (icon.data && icon.data.url) {
							return `${import.meta.env.VITE_APP_FOLDER_PATH}/${icon.data.url}`;
						}
						return null;
					})
					.filter(Boolean) as string[];

				setClientIconsUrls(clientIconsUrls);
			} catch (error) {
				logger.error("Error fetching client icons:", error);
				setError(
					error instanceof Error
						? error.message
						: "Failed to load client information"
				);
			} finally {
				setLoading(false);
			}
		};

		fetchClientIcons();
	}, []);

	useEffect(() => {
		const updateItemsPerView = () => {
			if (window.innerWidth < 640) {
				setItemsPerView(2);
			} else if (window.innerWidth < 768) {
				setItemsPerView(3);
			} else if (window.innerWidth < 1024) {
				setItemsPerView(4);
			} else {
				setItemsPerView(6);
			}
		};

		updateItemsPerView();
		window.addEventListener("resize", updateItemsPerView);
		return () => window.removeEventListener("resize", updateItemsPerView);
	}, []);

	useEffect(() => {
		if (clientIconsUrls.length > 6) {
			const timer = setInterval(() => {
				setCurrentIndex((prevIndex) =>
					(prevIndex + 1) % (clientIconsUrls.length - itemsPerView + 1)
				);
			}, 4000);
			return () => clearInterval(timer);
		} else {
			setCurrentIndex(0); // Always show first slide if not enough items
		}
	}, [itemsPerView, clientIconsUrls.length]);

	const visibleClients = clientIconsUrls.slice(
		currentIndex,
		currentIndex + itemsPerView
	);

	return (
		<section className="py-20 bg-gradient-to-b from-[#e6f9f3] to-white relative overflow-hidden">
			{/* Background pattern */}
			<div
				className="absolute inset-0 opacity-[0.03]"
				style={{
					backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%2310b981' fill-opacity='0.4'%3E%3Cpath d='M30 30h30v30H30z'/%3E%3C/g%3E%3C/svg%3E")`,
				}}
			></div>

			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
				<div className="text-center mb-16">
					<div className="inline-flex items-center px-6 py-3 rounded-full bg-gradient-to-r from-primary-600/10 to-forest-custom/10 border border-primary-600/20 mb-6">
						<span className="text-lg sm:text-xl text-primary-600">{sectionTag}</span>
					</div>
					<h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight FontStyles mb-3 sm:mb-4">
						<span className="text-gray-900 block">{sectionTitleLine1}</span>
						<span className="bg-gradient-to-r from-primary-600 to-forest-custom text-green-600 bg-clip-text block">{sectionTitleLine2}</span>
					</h2>
					<p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
						{sectionDescription}
					</p>
				</div>

				{/* Client logos grid or centered row if less than 6 */}
				{visibleClients.length < 6 ? (
					<div className="flex justify-center items-center gap-8 mb-16">
						{visibleClients.map((url, index) => (
							<div
								key={url + "-" + index}
								className="group flex items-center justify-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl border border-gray-100 hover:border-primary-200 transition-all duration-300 transform hover:scale-105"
								data-testid={`client-logo-${index}`}
							>
								<img
									src={url}
									alt={`Client logo ${index + 1}`}
									className="max-h-12 w-auto object-contain grayscale group-hover:grayscale-0 opacity-60 group-hover:opacity-100 transition-all duration-300"
									onError={(e) => {
										// Fallback to index if logo fails to load
										const target = e.target as HTMLImageElement;
										target.style.display = "none";
										const fallback =
											target.parentElement?.querySelector(
												".fallback-text"
											) as HTMLElement;
										if (fallback) {
											fallback.style.display = "block";
										}
									}}
								/>
								<div className="fallback-text hidden text-lg font-bold text-gray-700">
									{`Client ${index + 1}`}
								</div>
							</div>
						))}
					</div>
				) : (
					<div className="grid grid-cols-2 gap-4 sm:flex sm:justify-center sm:items-center sm:gap-8 mb-16">
						{visibleClients.map((url, index) => (
							<div
								key={url + "-" + index}
								className="group flex items-center justify-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl border border-gray-100 hover:border-primary-200 transition-all duration-300 transform hover:scale-105"
								data-testid={`client-logo-${index}`}
							>
								<img
									src={url}
									alt={`Client logo ${index + 1}`}
									className="max-h-12 w-auto object-contain grayscale group-hover:grayscale-0 opacity-60 group-hover:opacity-100 transition-all duration-300"
									onError={(e) => {
										// Fallback to index if logo fails to load
										const target = e.target as HTMLImageElement;
										target.style.display = "none";
										const fallback =
											target.parentElement?.querySelector(
												".fallback-text"
											) as HTMLElement;
										if (fallback) {
											fallback.style.display = "block";
										}
									}}
								/>
								<div className="fallback-text hidden text-lg font-bold text-gray-700">
									{`Client ${index + 1}`}
								</div>
							</div>
						))}
					</div>
				)}

				{/* Stats section */}
				<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
					<div className="text-center">
						<div className="text-4xl md:text-5xl font-bold text-primary-600 mb-2">
							{globalBrandsServed}
						</div>
						<div className="text-gray-800 font-medium">
							Global Brands Served
						</div>
					</div>
					<div className="text-center">
						<div className="text-4xl md:text-5xl font-bold text-forest-custom mb-2">
							{impressionsGenerated}
						</div>
						<div className="text-gray-800 font-medium">
							Impressions Generated
						</div>
					</div>
					<div className="text-center">
						<div className="text-4xl md:text-5xl font-bold text-primary-600 mb-2">
							{satisfactionRate}%
						</div>
						<div className="text-gray-800 font-medium">
							Client Satisfaction Rate
						</div>
					</div>
				</div>

				{/* Floating elements for visual appeal */}
				<div className="absolute top-10 left-10 w-16 h-16 bg-gradient-to-r from-primary-600/10 to-forest-custom/10 rounded-full blur-xl animate-pulse"></div>
				<div className="absolute bottom-10 right-10 w-20 h-20 bg-gradient-to-r from-forest-custom/10 to-primary-600/10 rounded-full blur-xl animate-pulse delay-1000"></div>
			</div>
		</section>
	);
}